import { collection, doc, getDoc } from 'firebase/firestore';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { db } from '../firebase-config';

const ContestLeaderboard = () =>
{
    const navigate = useNavigate();
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(true);

    const PAGE_SIZE = 20;
    const { contestId, page } = useParams();
    const pageNum = Number(page);

    const startRank = (pageNum - 1) * PAGE_SIZE + 1;
    const endRank = pageNum * PAGE_SIZE;

    useEffect(() =>
    {
        let cancelled = false;

        const loadResults = async () =>
        {
            setLoading(true);
            const tempResults = [];
            const ref = collection(db, "contests", contestId.toString(), "contest_leaderboard");

            for (let i = startRank; i <= endRank; i++)
            {
                const snap = await getDoc(doc(ref, i.toString()));
                if (snap.exists())
                {
                    const data = snap.data();
                    tempResults.push({
                        Rank: i,
                        Handle: data.handle,
                        Score: data.score
                    });
                }
            }

            if (!cancelled)
            {
                setResults(tempResults);
                setLoading(false);
            }
        };

        loadResults();
        return () => { cancelled = true; };
    }, [contestId, pageNum]);

    const getRankColor = (rank) =>
    {
        switch (rank)
        {
            case 1: return 'text-yellow-600 font-bold';
            case 2: return 'text-gray-500 font-bold';
            case 3: return 'text-amber-600 font-bold';
            default: return 'text-gray-700';
        }
    };

    const getRankIcon = (rank) =>
    {
        switch (rank)
        {
            case 1: return '🏆';
            case 2: return '🥈';
            case 3: return '🥉';
            default: return `#${rank}`;
        }
    };

    if (loading)
    {
        return (
            <div className="max-w-4xl mx-auto p-4 text-center text-base sm:text-xl">
                Loading leaderboard...
            </div>
        );
    }

    return (
        <div className="px-2 sm:px-0">

            {/* BACK */}
            <button
                onClick={() => navigate("/contests")}
                className="mb-3 mt-2 sm:mb-4 ml-2 sm:ml-10 bg-gray-500 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded text-sm sm:text-base"
            >
                ← Back
            </button>

            <div className="max-w-6xl mx-auto p-3 sm:p-6">

                {/* HEADER */}
                <div className="mb-4 sm:mb-6">
                    <h2 className="text-xl sm:text-3xl font-bold">
                        Leaderboard
                    </h2>
                    <p className="text-sm sm:text-xl text-gray-600">
                        Contest {contestId}
                    </p>
                </div>

                {/* TABLE */}
                <div className="bg-white rounded-lg shadow-md">

                    {/* HEADER ROW */}
                    <div className="px-4 sm:px-6 py-2 border-b bg-gray-200">
                        <div className="grid grid-cols-5 gap-3 text-sm sm:text-base font-semibold">
                            <div>Rank</div>
                            <div className="col-span-3 text-center">Participant</div>
                            <div className="text-center">Score</div>
                        </div>
                    </div>

                    {/* BODY */}
                    <div className="divide-y divide-gray-200">
                        {results.map(result => (
                            <div
                                key={result.Rank}
                                className="px-4 sm:px-6 py-3 hover:bg-gray-50"
                            >
                                <div className="grid grid-cols-5 gap-3 items-center">

                                    <div className={`text-sm sm:text-2xl ${getRankColor(result.Rank)}`}>
                                        {getRankIcon(result.Rank)}
                                    </div>

                                    <div className="col-span-3 truncate text-center">
                                        <Link
                                            to={`/profile/${result.Handle}`}
                                            className="text-sm sm:text-lg text-blue-600 hover:underline"
                                        >
                                            {result.Handle}
                                        </Link>
                                    </div>

                                    <div className="text-sm sm:text-lg font-bold text-center text-blue-600">
                                        {result.Score} pts
                                    </div>

                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* PAGINATION – ALWAYS HORIZONTAL */}
                <div className="mt-4 sm:mt-6 flex justify-between items-center gap-2">

                    <button
                        disabled={pageNum === 1}
                        onClick={() =>
                            navigate(`/contest_leaderboard/${contestId}/${pageNum - 1}`)
                        }
                        className="px-3 sm:px-4 py-2 border rounded disabled:opacity-55 text-sm sm:text-base"
                    >
                        Previous
                    </button>

                    <div className="flex items-center gap-2">
                        <span className="text-sm sm:text-lg">Page</span>
                        <input
                            type="number"
                            min="1"
                            value={pageNum}
                            onChange={(e) =>
                            {
                                const p = Number(e.target.value);
                                if (p >= 1)
                                {
                                    navigate(`/contest_leaderboard/${contestId}/${p}`);
                                }
                            }}
                            className="w-16 sm:w-20 text-center border border-gray-400 rounded py-1"
                        />
                    </div>

                    <button
                        disabled={results.length < PAGE_SIZE}
                        onClick={() =>
                            navigate(`/contest_leaderboard/${contestId}/${pageNum + 1}`)
                        }
                        className="px-3 sm:px-4 py-2 border rounded disabled:opacity-55 text-sm sm:text-base"
                    >
                        Next
                    </button>

                </div>

                {/* FOOTNOTE */}
                <div className="mt-4 sm:mt-6 text-center text-xs sm:text-xl text-gray-700">
                    Rankings based on total score, with randomized tiebreaking while applying the rating changes.
                </div>

            </div>
        </div>
    );
};

export default ContestLeaderboard;